import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize, take, tap, firstValueFrom, map } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDistribution, ModelsService, ModelType } from '@services/models.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { convertEpochToDateString } from '@functions/functions';
import { CreateAssetDistributionComponent } from 'src/app/dialogs/create-asset-distribution/create-asset-distribution.component';
import { MatSelect } from '@angular/material/select';
import { HttpErrorResponse } from '@angular/common/http';
import { AssetModelsComponent } from 'src/app/dialogs/asset-models/asset-models.component';

/** Used to filter the table of models */
export interface FilterModelData {
	Automatic: boolean;
	Manual: boolean;
	Active: boolean;
	Disabled?: boolean;
	Enabled?: boolean;
	// Location?: OrganisationsHierarchy;
}

/**
 * ModelsComponent
 */
@Component({
	selector: 'app-models',
	templateUrl: './models.component.html',
	styleUrls: ['./models.component.scss']
})
export class ModelsComponent implements OnInit {
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountID */
	public accountGUID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	public OrganisationUID: string;

	/** displayedColumns */
	displayedColumns: string[] = ['AssetName', 'CreatedAt', 'StartPoint', 'EndPoint', 'Type', 'CreatedBy', 'MostRecent', 'Options', 'GoToAsset']

	/** displayedAssetColumns */
	displayedAssetColumns: string[] = [
		'Name',
		'Description',
		'HierarchyPath',
		'Buttons',
		'GoToAsset'
	];

	/** filteredAssetColumns */
	filteredAssetColumns: string[] = this.displayedAssetColumns

	/** datasource */
	modelsDataSource: MatTableDataSource<AssetDistribution>;
	modelsReturned: boolean = false;

	/** datasource */
	assetsDataSource: MatTableDataSource<LevelAsset>;
	assetsReturned: boolean = false;

	/** Table paginator */
	@ViewChild("MatModelPaginator") MatModelPaginator: MatPaginator;

	/** Table sorting */
	@ViewChild("modelSort") MatModelSort: MatSort;

	/** Table paginator */
	@ViewChild("MatAssetPaginator") MatAssetPaginator: MatPaginator;
	/** Table sorting */
	@ViewChild("assetsSort") MatAssetSort: MatSort;

	/** currentUser */
	public currentUser: WebAppUser;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** hierarchyBreadcrumbs */
	private hierarchyBreadcrumbs: Map<number, string>;

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationDropdown */
	@ViewChild('locationdropdown') locationdropdown: MatSelect;

	/** modelTypeDropdown */
	@ViewChild('dropdownModelTypes') modelTypeDropdown: MatSelect;

	/** List of Tab IDs */
	public tabIDList: string[] = ["Model", "NoModel"]

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** locationFields */
	public modelTypeFields: { text: string, value: string } = { text: "ModelName", value: "ModelTypeID" };

	/** assetDistributions */
	public assetDistributions: AssetDistribution[] = [];

	/** filteredAssetDistributions */
	public filteredAssetDistributions: AssetDistribution[] = [];

	/** models */
	public models: any[] = [];

	/** Table fields */
	public modelFields: Object = { text: 'RuleDescription', id: 'RuleID' };

	/** Table fields */
	public assetFields: Object = { text: 'Name', id: 'AssetID' };

	/** filterLocationAssetIDs */
	filterLocationAssetIDs: number[] = [];

	/** locationHierarchyFilter */
	public locationHierarchyFilter?: OrganisationsHierarchy;

	/** selectedModelType */
	public selectedModelType: ModelType;

	/** assetList */
	public assetList: LevelAsset[] = [];

	/** Initialised filterData */
	public filterModelData: FilterModelData = {
		Automatic: false,
		Manual: false,
		Active: true,
		// Location: undefined
	}

	/** hasFilters */
	public hasFilters: boolean = false;

	/** selectedTab */
	public selectedTab: number = 0

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param route route
	 * @param globalFunctions globalFunctions
	 * @param modelsService modelsService
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		public modelsService: ModelsService
	) { }

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);

		this.setupModelCallbacks();
		this.setupAssetCallbacks();

		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(),
			this.siteSubscriptions.selectedAccount$.pipe(),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(),
			this.siteSubscriptions.hierarchicalData$.pipe(),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(take(1)),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!hierarchicalData && !!currentUser && !!queryParamMap)
		).subscribe({
			next: async ([selectedAccountGUID, account, selectedOrganisationUID, hierarchicalData, currentUser, queryParamMap]) => {
				this.accountGUID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.OrganisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('models') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.currentUser = currentUser;

				this.hierarchy = JSON.parse(hierarchicalData);
				this.hierarchyBreadcrumbs = this.flattenAssetBreadcrumbs(this.hierarchy);
				// this.assetList = this.flattenAssets(this.hierarchy);

				if (this.hierarchy[0].SubChild) {
					this.hierarchy[0].SubChild.forEach(subchild => {
						this.MainLocations.push(subchild);
					});
				}

				var savedTab = queryParamMap.get('selectedTab') ?? ""
				this.selectedTab = this.tabIDList.indexOf(savedTab)

				await this.getAssets();
				await this.getModelTypes();
				await this.getModels(this.selectedModelType);

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getModelTypes
	 */
	async getModelTypes() {

		try {
			const modelTypes = await firstValueFrom(
				this.modelsService.getModelTypes$(this.accountGUID, this.OrganisationUID).pipe(
					filter(result => !!result),
					tap(() => { }),
					finalize(() => {
						this.globalFunctions.displaySpinner(false)
					})
				)
			);
			if (modelTypes) {
				this.modelTypes = modelTypes;
				if (this.modelTypes.length == 1) {
					this.selectedModelType = this.modelTypes[0];

				}
			}
		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelTypes$", false);
		}

	}

	/**
	 * getModels
	 * @param modelType modelType  
	 */
	async getModels(modelType: ModelType) {

		try {
			const distributions = await firstValueFrom(
				this.modelsService.getAssetDistribution$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter(result => !!result),
						tap(() => { }),
						finalize(() => {
							this.globalFunctions.displaySpinner(false)
						})
					)
			);
			if (distributions) {
				this.assetDistributions = distributions.map((x: AssetDistribution) => ({
					...x,
					AssetName: this.getAssetName(x.AssetID)
				}));;
				this.models = this.assetDistributions;
				this.modelsDataSource.data = this.models;

				setTimeout(() => {
					this.setupModelCallbacks()
				}, 10);

			}
			this.modelsReturned = true;

		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) {
				this.assetDistributions = [];
				this.models = this.assetDistributions;
				return;
			}
			this.globalFunctions.HandleError(error, "Error: this.modelsService.getAssetDistribution$", false);
		}
	}

	/**
		 * get the Organisation Assets
		 */
	async getAssets() {

		try {

			const assets = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
						// , map(result =>
						// 	result
						// 		.filter((x: LevelAsset) => x.ModelCount === 0)
						// 		.map((x: LevelAsset) => {
						// 			x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						// 			return x;
						// 		})
						// )
					)
			);

			if (assets) {
				this.assetList = (<LevelAsset[]>assets)
				this.assetsDataSource.data = this.assetList.filter((x: LevelAsset) => x.ModelCount === 0)
					.map((x: LevelAsset) => {
						x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						return x;
					});;

				setTimeout(() => {
					this.setupAssetCallbacks();
				}, 10);


				if (this.assetsDataSource.sort) {
					this.assetsDataSource.sort.active = 'Name'; // Default column to sort by
					this.assetsDataSource.sort.direction = 'asc'; // Default sorting direction
				}
			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Asset List",
					duration: 5000,
					action: ""
				});
			}

			this.assetsReturned = true;

		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.assetsDataService.getOrganisationAssets$");
			}
		} finally {

		}
	}

	/**
	 * selectModelType
	 * @param event event 
	 */
	selectModelType(event: any) {
		if (event.value) {
			this.selectedModelType = event.value;
			this.getModels(this.selectedModelType);
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * Getting list of assets from an input hierarchy 
	 * @param hierarchy OrganisationsHierarchy
	 * @returns 
	 */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Returns a map of AssetID -> Hierarchy breadcrumb
	 */
	flattenAssetBreadcrumbs(
		hierarchy: OrganisationsHierarchy[],
		path: string[] = [],
		result = new Map<number, string>()
	): Map<number, string> {

		if (!Array.isArray(hierarchy)) {
			return result;
		}

		for (const item of hierarchy) {

			const currentPath = [...path, item.Name];

			// Store breadcrumb for every asset at this level
			for (const asset of item.Assets ?? []) {
				result.set(asset.AssetID, currentPath.join(' > '));
			}

			// Recurse into children
			this.flattenAssetBreadcrumbs(
				item.SubChild,
				currentPath,
				result
			);
		}

		return result;
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAssetName(assetID: number): string {
		let asset = this.assetList.find((x: LevelAsset) => x.AssetID == assetID);
		if (asset) {
			return `${asset.Name} - ${asset.Description}`;
		} else {
			return "";
		}
	}

	/** applyModelFilter */
	applyModelFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.modelsDataSource.filter = filterValue.trim().toLowerCase();

		if (this.modelsDataSource.paginator) {
			this.modelsDataSource.paginator.firstPage();
		}
	}

	/** applyAssetFilter */
	applyAssetFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.assetsDataSource.filter = filterValue.trim().toLowerCase();

		if (this.assetsDataSource.paginator) {
			this.assetsDataSource.paginator.firstPage();
		}
	}

	/**
	 * filterByLocation
	 * @param event event 
	 * @param cancel cancel 
	 */
	filterByLocation(event: any, cancel?: boolean) {
		this.filterLocationAssetIDs = [];
		if (cancel) {
			this.locationHierarchyFilter = undefined;
			this.locationdropdown.value = null;
		} else if (event.value) {
			this.locationHierarchyFilter = <OrganisationsHierarchy>event.value;
			this.flattenAssets([this.locationHierarchyFilter]).forEach(asset => {
				this.filterLocationAssetIDs.push(asset.AssetID);
			});
		}
		this.filterModels();
		this.filterAssets();
	}

	/**
	 * filterModels
	 * @param [doNotReset=false] 
	 */
	filterModels(doNotReset: boolean = false) {
		// Force MatTableDataSource to re-run filterPredicate
		const currentFilter = this.modelsDataSource.filter;
		this.modelsDataSource.filter = '';
		this.modelsDataSource.filter = currentFilter + ' ' || ' ';
	}

	/** Filters rule table based on filterData */
	filterAssets() {
		// Force MatTableDataSource to re-run filterPredicate
		const currentFilter = this.assetsDataSource.filter;
		this.assetsDataSource.filter = '';
		this.assetsDataSource.filter = currentFilter + ' ' || ' ';
	}

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {
		this.selectedTab = event.selectedIndex;
		let tabName = this.tabIDList[event.selectedIndex];
		switch (event.selectedIndex) {
			case 0:
				setTimeout(() => {
					this.setupModelCallbacks()
				}, 10);

				break;
			case 1:
				setTimeout(() => {
					this.setupAssetCallbacks();
				}, 10);
				break;
		}
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { selectedTab: tabName },
			queryParamsHandling: 'merge'
		});
	}

	/**
	 * setupModelCallbacks
	 */
	setupModelCallbacks() {

		if (!this.modelsDataSource) {
			this.assetDistributions = [];
			this.modelsDataSource = new MatTableDataSource(this.assetDistributions);
		}

		this.modelsDataSource.paginator = this.MatModelPaginator;
		this.modelsDataSource.sort = this.MatModelSort;

		this.modelsDataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'CreatedByUserSub': return item.CreatedByUserSub || '';
				case 'AssetName': return item.AssetName || '';
				case 'CreatedAt': return item.CreatedAt || '';
				case 'StartPoint': return item.StartPoint || '';
				case 'EndPoint': return item.EndPoint || '';
				default: return '';
			}
		};

		this.modelsDataSource.filterPredicate = (model: AssetDistribution, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if (
				(this.filterModelData.Automatic && !model.IsAutomatic) ||
				(this.filterModelData.Manual && model.IsAutomatic) ||
				(this.filterModelData.Active && !model.MostRecent) ||
				(this.filterModelData.Disabled && model.Disabled) ||
				(this.filterModelData.Enabled && !model.Disabled)) {
				return false;
			}

			if (this.locationHierarchyFilter) {
				if (!this.filterLocationAssetIDs.includes(model.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			let name: string = "";
			let asset = this.assetList.find((y: LevelAsset) => y.AssetID == model.AssetID);
			if (asset) { name = `${asset.Name}${asset.Description}`; }
			let createdBy: string = model.CreatedByUserSub;
			let createdAt: string = this.convertEpoch(model.CreatedAt);
			let startPoint: string = this.convertEpoch(model.StartPoint);
			let endPoint: string = this.convertEpoch(model.EndPoint);
			const textToSearch = `${name}${createdBy}${createdAt}${startPoint}${endPoint}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}

	/**
	 * setupAssetCallbacks
	 */
	setupAssetCallbacks() {

		if (!this.assetsDataSource) {
			this.assetList = [];
			this.assetsDataSource = new MatTableDataSource(this.assetList);
		}

		this.assetsDataSource.paginator = this.MatAssetPaginator;
		this.assetsDataSource.sort = this.MatAssetSort;

		this.assetsDataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Name': return item.Name || '';
				case 'Description': return item.Description || '';
				default: return '';
			}
		};

		this.assetsDataSource.filterPredicate = (asset: LevelAsset, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if (this.locationHierarchyFilter) {
				if (!this.filterLocationAssetIDs.includes(asset.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			const textToSearch =
				`${asset.Name ?? ''}${asset.Description ?? ''}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}

	/**
	 * setFilterOption
	 * @param option option 
	 * @param value value 
	 */
	setFilterOption(option: string, value: boolean) {
		switch (option) {
			case 'automatic only':
				this.filterModelData.Automatic = value;
				break;
			case 'manual only':
				this.filterModelData.Manual = value;
				break;
			case 'most recent':
				this.filterModelData.Active = value;
				break;
			case 'disabled':
				this.filterModelData.Disabled = value;
				break;
			case 'enabled':
				this.filterModelData.Enabled = value;
				break;
			default:
				break;
		}
		this.filterModels();
	}

	/**
	 * goToModel
	 * @param model model 
	 */
	goToModel(model: any) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/models/${model.AssetDistributionID}`],
			{ queryParams: { 'modelType': this.selectedModelType.ModelTypeID } });
		return;

		if (this.selectedModelType.ModelTypeID == 1) {
			let data = {
				asset: this.getAsset(model.AssetID),
				viewing: true,
				distributionID: model.AssetDistributionID,
				existingDistribution: model
			}

			const dialogRef = this.dialog.open(CreateAssetDistributionComponent
				, { data: data }
			);

			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
			});
		}
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAsset(assetID: number): LevelAsset | undefined {
		return this.assetList.find((x: LevelAsset) => x.AssetID == assetID);
	}

	/**
	 * disableModel
	 * @param model model  
	 * @param enable enable
	 */
	disableModel(model: any, enable?: boolean) {
		this.globalFunctions.UserConfirm$("Are you sure you want to delete this model?").subscribe(async OKResult => {
			if (OKResult) {
				if (this.selectedModelType.ModelTypeID == 1) {
					// Create a local Subject to control the subscription lifecycle
					const fetchDataSubject = new Subject<void>();

					this.modelsService.patchOldDistribution$(this.accountGUID, this.OrganisationUID, model.AssetDistributionID, model.AssetID, false, enable == true ? false : true).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((response) => !!response),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
							this.getModels(this.selectedModelType);
						})
					).subscribe({
						next: (response) => {
							this.globalFunctions.DisplaySnackBar({
								message: enable == true ? "Model enabled" : "Model deleted",
								duration: 2000,
								action: ""
							})
							this.getModels(this.selectedModelType);
						},
						error: (error) => { this.globalFunctions.HandleError(error, "Error: this.modelsService.patchOldDistribution$", false); }
					});
				}
			}
		});
	}

	/**
	 * activateModel
	 * @param model model  
	 * @param deactivate deactivate 
	 */
	activateModel(model: any, deactivate: boolean = false) {
		if (this.selectedModelType.ModelTypeID == 1) {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.modelsService.patchOldDistribution$(this.accountGUID, this.OrganisationUID, model.AssetDistributionID, model.AssetID, deactivate ? false : true, false).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.getModels(this.selectedModelType);
				})
			).subscribe({
				next: (response) => {
					this.globalFunctions.DisplaySnackBar({
						message: deactivate ? "Model deactivated" : "Model activated",
						duration: 2000,
						action: ""
					})
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.modelsService.patchOldDistribution$", false); }
			});
		}
	}

	/**
	 * recreateModel
	 * @param model model 
	 */
	recreateModel(model: any) {
		if (this.selectedModelType.ModelTypeID == 1) {
			let data = {
				asset: this.getAsset(model.AssetID),
				viewing: false,
				editing: true,
				distributionID: model.AssetDistributionID,
				existingDistribution: model
			}

			const dialogRef = this.dialog.open(CreateAssetDistributionComponent
				, { data: data }
			);

			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
				this.patchDistribution(model, result.tolerance, result.sensitivity, result.recreationFrequency, result.overlapPeriod);
			});
		}
	}

	/**
	 * patchDistribution
	 * @param distribution distribution
	 * @param tolerance tolerance 
	 * @param sensitivity sensitivity 
	 * @param recreationFrequency recreationFrequency 
	 * @param overlapPeriod overlapPeriod 
	 */
	patchDistribution(distribution: AssetDistribution, tolerance: number, sensitivity: number, recreationFrequency: number, overlapPeriod: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchDistribution$(this.accountGUID, this.OrganisationUID, distribution.AssetDistributionID,
			tolerance, sensitivity, recreationFrequency, overlapPeriod).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Distribution Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.siteSubscriptions.UpdateModelList(true);
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}

	/**
	 * goToAsset
	 * @param row 
	 */
	goToAsset(row: any) {
		let asset = this.getAsset(row.AssetID);
		if (asset) {
			this.siteSubscriptions.SetSelectedAsset(asset);
			this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
		}
	}

	/**
	 * clearAllFilters
	 */
	clearAllFilters() {
		this.filterModelData = {
			Automatic: false,
			Manual: false,
			Active: false
		}
		this.locationHierarchyFilter = undefined;
		this.filterByLocation(undefined, true);
		this.filterModels();
	}

	/**
	 * getFilterMessage
	 * @returns 
	 */
	getFilterMessage(): string {
		this.hasFilters = false;
		let showingResult: string = "Showing ";
		let amount: number = 0;
		let hasLocation: boolean = false;
		Object.entries(this.filterModelData).forEach(([key, value]) => {
			if (value) {
				this.hasFilters = true;
				if (key != "Location") {
					if (amount == 0) {
						showingResult += " " + key
					} else {
						showingResult += ", " + key
					}
					amount++;
				}
			}
		});
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		if (amount == 0) {
			return showingResult + (hasLocation ? `all models in ${this.locationHierarchyFilter?.Name}` : "all models");
		}
		return showingResult + (hasLocation ? " models in " + this.locationHierarchyFilter?.Name : " models");
	}

	/**
	 * getLocationMessage
	 * @returns 
	 */
	getLocationMessage(): string {
		let result: string = "Showing ";
		let hasLocation: boolean = false;
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		return result + (hasLocation ? "Assets in " + this.locationHierarchyFilter?.Name : "All Assets");
	}

	/**
	 * ManageModels
	 * Open Models List as a pop up dialog and manage models for the selected asset based on the result
	 */
	ManageModels(selectedAsset: LevelAsset) {

		let data = {
			asset: selectedAsset
		}

		const dialogRef = this.dialog.open(AssetModelsComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			this.getModels(this.selectedModelType);
		});
	}

}
